import type { Commitment } from '@finapp/types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { getCostpointLabel } from '../utils/costpointLabels';

interface EditCommitmentModalProps {
  commitment: Commitment | null; // null when closed
  // The budget period's period_type (H1/H2/ANNUAL) this commitment is attached to - shown
  // alongside costpoint/type so it's clear where this commitment counts toward, same as the
  // listing. Resolved by the caller (Forecast.tsx) since a Commitment only carries the id.
  periodLabel: string;
  onClose: () => void;
  onSave: (id: number, data: { description: string; amount: number }) => Promise<void>;
}

// Mirrors CostpointBudgetEditPage.tsx's EditItemForm: editing a commitment only ever
// touches description/amount, same as editing a budget item - costpoint/type/period are
// fixed at creation time, no need to re-pick them here.
const EditCommitmentModal: React.FC<EditCommitmentModalProps> = ({
  commitment,
  periodLabel,
  onClose,
  onSave,
}) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (commitment) {
      setDescription(commitment.description);
      setAmount(commitment.amount.toString());
      setError(null);
    }
  }, [commitment]);

  if (!commitment) {
    return null;
  }

  const parsedAmount = parseFloat(amount);
  const isValid =
    description.trim().length > 0 && amount.trim().length > 0 && !Number.isNaN(parsedAmount) && parsedAmount > 0;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(commitment.id, { description: description.trim(), amount: parsedAmount });
      onClose();
    } catch {
      setError('Failed to save the commitment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center"
      data-testid="edit-commitment-overlay"
    >
      <div
        className="relative top-4 sm:top-10 p-4 sm:p-5 border w-full max-w-md shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto m-4 sm:m-0"
        role="dialog"
        data-testid="edit-commitment-modal"
      >
        <h3 className="text-lg font-medium text-gray-900 mb-1">Edit Commitment</h3>
        <p className="text-sm text-gray-500 mb-4">
          {getCostpointLabel(commitment.costpoint)} ·{' '}
          {commitment.type === 'expenses' ? 'Expense' : 'Income'} · {periodLabel}
        </p>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="edit-commitment-description-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (€)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="edit-commitment-amount-input"
            />
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="edit-commitment-submit-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCommitmentModal;
